import { useState } from 'react';
import { Wrench, ShieldAlert, KeyRound } from 'lucide-react';
import apiClient from '@/api/client';
import { passkeyAnmelden, passkeysMoeglich } from '@/api/passkeys.api';
import { useAdminAuth, type AdminUser } from '@/store/useAdminAuth';

// ─── Anmeldung an der Betreiberkonsole ───────────────────────────────────────
//
// Getrennt von der Anwendung: wer dort angemeldet ist, ist hier nicht
// angemeldet. Das ist beabsichtigt – dieses Konto erreicht jede Kundenfamilie.

interface Anmeldeantwort {
  user?: AdminUser;
  accessToken: string;
  csrfToken: string;
  requiresTwoFactor?: boolean;
}

export function LoginPage() {
  const anmelden = useAdminAuth((s) => s.anmelden);

  const [email, setEmail] = useState('');
  const [passwort, setPasswort] = useState('');
  const [code, setCode] = useState('');
  const [zweiFaktorNoetig, setZweiFaktorNoetig] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);

  /**
   * Was nach einer erfolgreichen Anmeldung passiert – gleich, auf welchem Weg.
   *
   * Die Rollenprüfung steht hier und nicht nur im Passwortzweig: sonst käme
   * man mit einem Passkey an ihr vorbei in eine Oberfläche, in der jede Route
   * mit 403 antwortet.
   */
  const uebernehmen = async (data: Anmeldeantwort): Promise<void> => {
    if (data.user?.role !== 'admin') {
      // Serverseitig weist requireRole ohnehin ab; die Meldung hier erspart
      // dem Nutzer den Umweg über eine leere Oberfläche.
      //
      // Die Anmeldung war technisch erfolgreich – der Server hat also einen
      // Refresh-Cookie für dieses Konto gesetzt. Der wird hier wieder
      // abgeräumt: eine Sitzung, die niemand nutzen darf, soll auch nicht
      // liegen bleiben und beim nächsten Laden still wiederhergestellt
      // werden. (Der Abmeldung liegt CSRF-Schutz vor, deshalb der Token aus
      // der Antwort.)
      // Leeres Objekt, nicht null: der Client setzt Content-Type
      // application/json, und `null` ginge als Zeichenkette "null" hinaus –
      // die der Body-Parser zurückweist.
      await apiClient
        .post('/auth/logout', {}, { headers: { 'X-CSRF-Token': data.csrfToken } })
        .catch(() => {});
      setFehler('Dieses Konto hat keinen Zugang zur Betreiberkonsole.');
      return;
    }

    anmelden(data.user, data.accessToken, data.csrfToken);
  };

  const absenden = async (e: React.FormEvent) => {
    e.preventDefault();
    setFehler(null);
    setLaeuft(true);

    try {
      const { data } = await apiClient.post('/auth/login', {
        email,
        password: passwort,
        ...(code ? { totpCode: code } : {}),
      });

      if (data.requiresTwoFactor) {
        setZweiFaktorNoetig(true);
        setFehler(null);
        return;
      }

      await uebernehmen(data as Anmeldeantwort);
    } catch (err) {
      setFehler(err instanceof Error ? err.message : 'Anmeldung fehlgeschlagen.');
    } finally {
      setLaeuft(false);
    }
  };

  // ── Anmeldung mit Passkey ────────────────────────────────────────────────
  //
  // Ohne Passwort: der Schlüssel weist das Konto selbst aus. Ein
  // Sicherheitsschlüssel am USB-Anschluss – etwa ein YubiKey – zählt dazu.
  const mitPasskey = async () => {
    if (!email) {
      setFehler('Bitte zuerst die E-Mail-Adresse eintragen.');
      return;
    }
    setFehler(null);
    setLaeuft(true);
    try {
      await uebernehmen((await passkeyAnmelden(email)) as Anmeldeantwort);
    } catch (err) {
      const e = err as Error;
      // Abbruch durch den Nutzer ist kein Fehler.
      if (e.name === 'NotAllowedError' || /abort|not allowed/i.test(e.message)) {
        setFehler(null);
        return;
      }
      setFehler(e.message || 'Mit diesem Schlüssel war keine Anmeldung möglich.');
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-900 px-4">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-slate-800 ring-1 ring-slate-700">
            <Wrench className="h-5 w-5 text-slate-300" aria-hidden="true" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-white">Betrieb</h1>
            <p className="text-xs text-slate-400">Stammbaum-Installation</p>
          </div>
        </div>

        <form
          onSubmit={absenden}
          className="rounded-xl border border-slate-700 bg-slate-800 p-5 shadow-xl"
        >
          <label htmlFor="email" className="block text-xs font-medium text-slate-300">
            E-Mail
          </label>
          <input
            id="email"
            type="email"
            required
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1.5 w-full rounded-lg border border-slate-600 bg-slate-900 px-3 py-2 text-sm text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />

          <label htmlFor="passwort" className="mt-4 block text-xs font-medium text-slate-300">
            Passwort
          </label>
          <input
            id="passwort"
            type="password"
            required
            autoComplete="current-password"
            value={passwort}
            onChange={(e) => setPasswort(e.target.value)}
            className="mt-1.5 w-full rounded-lg border border-slate-600 bg-slate-900 px-3 py-2 text-sm text-white focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />

          {zweiFaktorNoetig && (
            <>
              <label htmlFor="code" className="mt-4 block text-xs font-medium text-slate-300">
                Code aus der Authenticator-App oder Ersatzcode
              </label>
              <input
                id="code"
                autoComplete="one-time-code"
                autoCapitalize="characters"
                spellCheck={false}
                autoFocus
                value={code}
                // Ohne `inputMode="numeric"`: hier darf auch ein Ersatzcode
                // stehen, und der besteht aus Buchstaben.
                onChange={(e) => setCode(e.target.value)}
                className="mt-1.5 w-full rounded-lg border border-slate-600 bg-slate-900 px-3 py-2 text-center font-mono text-lg tracking-[0.3em] text-white focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <p className="mt-1.5 text-xs text-slate-400">
                Kein Zugriff auf die App? Ein Ersatzcode (Form ABCDE-FGHJK) tut es auch – er
                gilt genau einmal.
              </p>
            </>
          )}

          {fehler && (
            <p className="mt-4 flex items-start gap-2 rounded-lg bg-red-950 p-2.5 text-xs leading-relaxed text-red-300 ring-1 ring-red-900">
              <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              {fehler}
            </p>
          )}

          <button
            type="submit"
            disabled={laeuft}
            className="mt-5 w-full rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-500 disabled:opacity-50"
          >
            {laeuft ? 'Wird geprüft…' : zweiFaktorNoetig ? 'Bestätigen' : 'Anmelden'}
          </button>
          {passkeysMoeglich() && (
            <>
              <div className="mt-5 flex items-center gap-3">
                <span className="h-px flex-1 bg-slate-700" />
                <span className="text-[11px] uppercase tracking-wide text-slate-500">oder</span>
                <span className="h-px flex-1 bg-slate-700" />
              </div>
              <button
                type="button"
                onClick={mitPasskey}
                disabled={laeuft}
                className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg border border-slate-600 px-4 py-2.5 text-sm font-medium text-slate-200 hover:bg-slate-700 disabled:opacity-50"
              >
                <KeyRound className="h-4 w-4" aria-hidden="true" />
                Mit Passkey oder Sicherheitsschlüssel
              </button>
            </>
          )}
        </form>

        <p className="mt-4 text-center text-xs leading-relaxed text-slate-500">
          Diese Anmeldung gilt nur hier. Für den Stammbaum selbst melden Sie sich
          getrennt an.
        </p>
      </div>
    </div>
  );
}
